import {
    Component,
    Input,
    OnDestroy,
    WritableSignal,
    computed,
    effect,
    signal,
} from '@angular/core';
import { Process } from '@em4cloud/process-tree/lib/model/model';
import { Area, BusinessProcess, BusinessProcessTask } from 'app/api';
import { ProcessService } from 'app/modules/bp-management/process.service';
import { DataManagementAPIService } from 'app/modules/data-module/data-management/data-management.api.service';
import { OrgService } from 'app/modules/org-sys-area/services/org.service';
import { VerifyService } from 'app/modules/verify/verify.service';
import { ViewService } from 'app/modules/view/view.service';
import { NewMDOService } from 'app/services/new_mdo.service';
import { Subject, takeUntil } from 'rxjs';
import createDeepCopy from '../../../babylonjs/core/createDeepCopy';
import { DependencyService } from '../../../svgjs/svg-flowchart/dependency.service';
import { FlowchartType } from '../../babylon-header/babylon-header.component';
import { FlowchartContainerConfig } from '../../flowchart/flowchart-container/flowchart-container.component';
import { PrintViewComponent } from '../print-view.component';

@Component({
    selector: 'lib-bp-recursive-print',
    templateUrl: './bp-recursive-print.component.html',
    styleUrl: '../print-view.component.scss',
    standalone: false,
})
export class BpRecursivePrintComponent extends PrintViewComponent implements OnDestroy {
    private _destroy$: Subject<void> = new Subject();

    businessProcess: BusinessProcess;
    /**
     * The process that contains the details and the description
     */
    @Input() processId: string;
    /**
     * The service that is needed for FlowchartContainerComponent. Either ViewService or VerifyService.
     */
    @Input() service: ViewService | VerifyService;

    @Input() isNested: boolean;

    taskDescription: 'instructions' | 'description' = 'instructions';

    /**
     * The signal used to execute actions
     */
    executeActionSignal = signal(null);

    flowchartConfig: FlowchartContainerConfig;

    technologies = computed(() => this._newMdoService.systems());
    dataTree = computed(() => this.dataService.data());

    process: WritableSignal<Process> = signal(null);
    tasks: WritableSignal<BusinessProcessTask[]> = signal([]);

    cameraScale = signal('100');

    static _isPrinting: boolean = false;

    get isPrinting(): boolean {
        return BpRecursivePrintComponent._isPrinting;
    }

    constructor(
        private _newMdoService: NewMDOService,
        private dataService: DataManagementAPIService,
        private organisationComponentService: OrgService,
        private dependencyService: DependencyService,
        private processService: ProcessService
    ) {
        super();

        BpRecursivePrintComponent._isPrinting = true;

        this.flowchartConfig = {
            flowchartType: 'flowchart2',
            service: this.service,
            executeActionSignal: signal(null),
        };

        effect(() => {
            if (!this.process()) {
                return;
            }
            const owner = this._newMdoService.usersForSelect.find(
                owner => owner.value === this.process().ownerid
            );
            this.process().owner = owner.label;

            const area: Area = this.findDeep(this.process().area, this._newMdoService.areas());
            this.process().area = area.title;

            switch (this.process().status) {
                case 'D':
                case 'DEVELOPMENT':
                    this.process().status = 'Development';
                    break;
                case 'A':
                case 'ACTIVE':
                    this.process().status = 'Active';
                    break;
                case 'I':
                case 'INACTIVE':
                    this.process().status = 'Inactive';
                    break;
                default:
                    this.process().status = 'N/A';
                    break;
            }
        });
    }

    ngOnInit(): void {
        const id = this.processId;
        const service = this.service;

        if (id && service) {
            // Get process
            this.process.set(this.processService.getProcessById(id));

            // Get tasks
            service
                .getBusinessProcessById(id)
                .pipe(takeUntil(this._destroy$))
                .subscribe(businessProcess => {
                    this.prepareTasks(businessProcess.tasks);
                });
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    private findDeep(id: string, items: any[]): any {
        let versionId: string;
        if (id.includes('_')) {
            versionId = id.split('_')[0];
            id = id.split('_')[1];
        }

        for (let item of items) {
            if (versionId && item['versionId'] && item['versionId'] !== versionId) {
                continue;
            }

            if (item['id'] === id) return item;

            if (item['children']?.length > 0) {
                let subItem = this.findDeep(id, item['children']);
                if (subItem) return subItem;
            }
        }
        return null;
    }

    static override print(title: string): void {
        setTimeout(() => {
            const print = this.printContextInNewWindow(title);

            print.newWindow.onafterprint = () => {
                print.newWindow.close();
                BpRecursivePrintComponent._isPrinting = false;
            };
        }, 100);
    }

    modifyTasks(tasks: BusinessProcessTask[]): BusinessProcessTask[] {
        tasks.forEach(task => {
            const org = this.organisationComponentService.getItemById(task.data.action.responsible);
            task.data.action.responsible = org?.title ?? 'N/A';
        });
        return tasks;
    }

    private prepareTasks(
        tasks: BusinessProcessTask[],
        flowchartType: FlowchartType = FlowchartType.all
    ): void {
        let copiedTasks: BusinessProcessTask[];
        if (tasks?.length) {
            copiedTasks = tasks.map(t => createDeepCopy(t));
            copiedTasks.map(t => (t.data.text1 = ''));
            copiedTasks.map(t => (t.data.text2 = ''));

            if (typeof this.modifyTasks !== 'undefined' && copiedTasks?.length) {
                copiedTasks = this.modifyTasks(copiedTasks);
            }

            // Setting indexes for dependencies
            copiedTasks = copiedTasks.map((t: BusinessProcessTask) => {
                if (t.data.action.dependencies?.length) {
                    t.data.action.dependencies = t.data.action.dependencies.map((d: any) => {
                        const taskId = d.id;
                        const index = copiedTasks.findIndex(t => t.id === taskId);
                        d.index = index;

                        return d;
                    });
                }

                return t;
            });

            /**
             * Filter entries by flowchart type
             * For all nothing should be filtered out
             * For technical fiDocument(9) should be filtered out
             * For financial ... should be filtered out
             */
            copiedTasks = copiedTasks.map(t => {
                if (flowchartType === FlowchartType.technical) {
                    t.data.input = t.data.input.filter(input => input.category !== 9);
                    t.data.output = t.data.output.filter(input => input.category !== 9);
                } else if (flowchartType === FlowchartType.financial) {
                    t.data.input = t.data.input.filter(input => input.category === 9);
                    t.data.output = t.data.output.filter(input => input.category === 9);
                }
                return t;
            });

            copiedTasks = copiedTasks.map(task => {
                const updateMetadataWithLogo = table => {
                    const data = this.findDeep(table.metadata['value'], this.dataTree());
                    if (!data) return table;

                    const technology = this.findDeep(data.system, this.technologies());
                    if (!technology) return table;

                    const root = this.findRootParent(this.technologies(), technology.id);
                    if (!root) return table;

                    return {
                        ...table,
                        metadata: {
                            ...table.metadata,
                            technologyLogo: root.metadata?.logo,
                        },
                    };
                };

                if (task.data.input?.length) {
                    task.data.input = task.data.input.map(input => {
                        return input.category === 7 ? updateMetadataWithLogo(input) : input;
                    });
                }
                if (task.data.output?.length) {
                    task.data.output = task.data.output.map(output => {
                        return output.category === 7 ? updateMetadataWithLogo(output) : output;
                    });
                }

                return task;
            });
            this.dependencyService.tasks = copiedTasks;

            copiedTasks = this.service.replaceSystemIdWithSystemTitle(copiedTasks);
        }

        this.tasks.set(copiedTasks && copiedTasks.length ? [...copiedTasks] : []);
    }

    private findParent(area: any, targetId: string): any | null {
        // If the current area matches the target ID, return this area (base case)
        if (area.id === targetId) {
            return area;
        }

        // Recursively check the children to find the target ID
        if (area.children) {
            for (let child of area.children) {
                const foundArea = this.findParent(child, targetId);
                if (foundArea) {
                    return foundArea;
                }
            }
        }

        return null; // Return null if the target ID is not found
    }

    private findRootParent(areas: any[], targetId: string): any | null {
        // Helper function to recursively find the parent of the area with the given ID

        if (!areas || !areas.length) {
            return null;
        }

        // Iterate over the top-level areas to find the root parent
        for (let area of areas) {
            const result = this.findParent(area, targetId);
            if (result) {
                // If the result is found, return the root (top-level area)
                return area;
            }
        }

        return null; // Return null if no area with the given ID is found
    }

    trackByFn(index: number, item: any) {
        return item.id || index;
    }
}
